import { useState, type FormEvent } from "react";
import { Upload } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { uploadAudio } from "@/api/client";

type VoiceSourceUploaderProps = {
  projectName: string;
  onUploadSuccess: (durationStr: string | null) => void;
};

export function VoiceSourceUploader({ projectName, onUploadSuccess }: VoiceSourceUploaderProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);

  const formatDuration = (seconds?: number) => {
    if (seconds == null) return null;
    if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, "0")}` : `${secs}s`;
  };

  const handleUpload = async (evt: FormEvent) => {
    evt.preventDefault();
    if (!file) { setUploadMessage("Choisissez un fichier audio"); return; }
    setUploadMessage("Analyse en cours…");
    try {
      const data = await uploadAudio(projectName, file);
      const dur = formatDuration((data.metadata as { duration?: number } | undefined)?.duration);
      setUploadMessage(`Importé${dur ? ` (${dur})` : ""}`);
      setFile(null);
      onUploadSuccess(dur);
    } catch (err) {
      setUploadMessage(`Erreur: ${(err as Error).message}`);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Upload className="h-4 w-4" />Importer une source vocale</CardTitle>
        <CardDescription>Fichiers audio (interviews, témoignages, ambiances inédites).</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleUpload} className="flex flex-col gap-3">
          <Input
            type="file"
            accept="audio/*"
            className="cursor-pointer"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setUploadMessage(null); }}
          />
          <div className="flex items-center gap-3">
            <Button type="submit" size="sm" disabled={!file}>
              <Upload className="mr-2 h-3.5 w-3.5" />
              Téléverser
            </Button>
            {uploadMessage && <span className="text-sm text-muted-foreground">{uploadMessage}</span>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
